import React, { useMemo, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { colKey, headerLabels, roomKey, rowInstalled } from "../components/FrsGridView";

/**
 * FT-3 — bulk edit of a selection (a collapsed group, or the ticked rooms). The system prepares
 * the change for every room; the person unticks what should not move; ONE "Stage" puts it all
 * into the same pending set the grid's Save sends as ONE revision batch.
 */
const roomLabelOf = (r) => {
  const name = (r.room_name || r.room_type || "").trim();
  const num = r.room_number != null && r.room_number !== "" ? String(r.room_number) : "";
  return name && num ? `${num} · ${name}` : num || name || "—";
};
const qtyOf = (rows) => rows.reduce((s, it) => s + Number(it.is_delta ? it.delta_qty || 0 : it.quantity || 0), 0);

export default function BulkEditDialog({ target, items, completions, pending, roomRefOf, onClose }) {
  const { label, rooms } = target;
  const roomKeys = useMemo(() => new Set(rooms.map((r) => r.key)), [rooms]);
  const inScope = useMemo(() => items.filter((it) => roomKeys.has(roomKey(it))), [items, roomKeys]);

  const columns = useMemo(() => {
    const m = new Map();
    for (const it of inScope) {
      const k = colKey(it);
      if (!m.has(k)) m.set(k, { key: k, scope: it.scope_type || "", uid: it.product_uid || "", label: headerLabels(it).part, rooms: 0 });
    }
    for (const r of rooms) {
      const seen = new Set(inScope.filter((it) => roomKey(it) === r.key).map(colKey));
      seen.forEach((k) => { m.get(k).rooms += 1; });
    }
    return [...m.values()].sort((a, b) => a.scope.localeCompare(b.scope) || a.label.localeCompare(b.label));
  }, [inScope, rooms]);

  const [mode, setMode] = useState("qty");
  const [colKeySel, setColKeySel] = useState(target.col?.key || columns[0]?.key || "");
  const [qty, setQty] = useState("");
  const [addMissing, setAddMissing] = useState(false);
  const [roomType, setRoomType] = useState("");
  const [off, setOff] = useState(new Set());
  const col = columns.find((c) => c.key === colKeySel);

  const preview = useMemo(() => {
    if (mode === "type") {
      return rooms.map((r) => {
        const current = r.room_type || "";
        return { key: r.key, room: r, label: roomLabelOf(r), current, next: roomType, same: current === roomType, ok: roomType.trim() !== "" };
      });
    }
    if (!col) return [];
    const n = qty === "" ? null : Math.max(0, Number(qty));
    return rooms.map((r) => {
      const rows = inScope.filter((it) => roomKey(it) === r.key && colKey(it) === col.key);
      const parent = rows.find((x) => !x.is_delta) || rows[0];
      const hasScope = inScope.some((it) => roomKey(it) === r.key && (it.scope_type || "") === col.scope);
      const installed = rows.reduce((s, it) => s + rowInstalled(it, completions[`${roomKey(it)}||${it.scope_type || ""}`]), 0);
      const current = qtyOf(rows);
      const missing = !parent;
      return {
        key: r.key, room: r, label: roomLabelOf(r), parent, current, next: n, installed, missing, hasScope,
        same: !missing && n === current,
        below: !missing && n !== null && n < installed,
        ok: n !== null && (missing ? (addMissing && hasScope && n > 0) : n >= installed && n !== current),
      };
    });
  }, [mode, rooms, inScope, col, qty, addMissing, roomType, completions]);

  const staged = preview.filter((p) => p.ok && !off.has(p.key));
  const toggle = (k) => setOff((s) => { const n = new Set(s); if (n.has(k)) n.delete(k); else n.add(k); return n; });

  const stage = () => {
    if (!staged.length) return;
    for (const p of staged) {
      if (mode === "type") {
        pending.stage(`r:${p.key}:room_type`, {
          op: { op: "set_room", room: roomRefOf(p.room), fields: { room_type: roomType } },
          room_label: p.label, label: `type “${p.current}” → “${roomType}”`,
        });
      } else if (p.missing) {
        pending.stage(`add:${p.key}:${col.uid || col.key}`, {
          op: { op: "add_product", room: roomRefOf(p.room, col.scope), scope_type: col.scope, product_uid: col.uid, qty: p.next },
          room_label: p.label, label: `add ${col.label} × ${p.next}`,
        });
      } else {
        pending.stage(`q:${p.parent.id}`, {
          op: { op: "set_quantity", item_id: p.parent.id, qty: p.next },
          room_label: p.label, label: `${col.label} ${p.current} → ${p.next}`,
        });
      }
    }
    toast.success(`${staged.length} change(s) waiting for Save`, { description: label });
    onClose();
  };

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-2xl" data-testid="frs-bulk-edit-dialog">
        <DialogHeader>
          <DialogTitle>Bulk edit — {label}</DialogTitle>
          <DialogDescription>
            {rooms.length} room{rooms.length === 1 ? "" : "s"}. Nothing is written until Save — this stages one change per ticked room.
          </DialogDescription>
        </DialogHeader>
        <div className="flex items-center gap-1 text-xs" data-testid="frs-bulk-mode">
          {[["qty", "Quantity"], ["type", "Room type"]].map(([v, l]) => (
            <button key={v} type="button" onClick={() => { setMode(v); setOff(new Set()); }} data-testid={`frs-bulk-mode-${v}`}
                    className={`h-7 px-3 rounded-full border ${mode === v ? "bg-gray-900 text-white border-gray-900" : "bg-white text-gray-700 border-gray-300"}`}>
              {l}
            </button>
          ))}
        </div>
        {mode === "qty" ? (
          <div className="grid sm:grid-cols-[1fr_120px] gap-3 items-end">
            <div>
              <Label className="text-xs">Product</Label>
              <select value={colKeySel} onChange={(e) => { setColKeySel(e.target.value); setOff(new Set()); }}
                      data-testid="frs-bulk-product" className="w-full h-9 rounded-md border border-gray-300 bg-white px-2 text-sm">
                {columns.map((c) => (
                  <option key={c.key} value={c.key}>{c.scope ? `${c.scope} · ` : ""}{c.label} — in {c.rooms} of {rooms.length}</option>
                ))}
              </select>
            </div>
            <div>
              <Label className="text-xs">Quantity per room</Label>
              <Input type="number" min={0} value={qty} onChange={(e) => setQty(e.target.value)} autoFocus
                     data-testid="frs-bulk-qty" className="h-9" />
            </div>
            {col && col.rooms < rooms.length && (
              <label className="sm:col-span-2 inline-flex items-center gap-2 text-xs text-gray-700">
                <input type="checkbox" checked={addMissing} onChange={(e) => setAddMissing(e.target.checked)} data-testid="frs-bulk-add-missing" />
                Also add {col.label} to the {rooms.length - col.rooms} room(s) that do not have it yet (only where the scope is already in the room)
              </label>
            )}
          </div>
        ) : (
          <div>
            <Label className="text-xs">New room type for every ticked room</Label>
            <Input value={roomType} onChange={(e) => setRoomType(e.target.value)} autoFocus data-testid="frs-bulk-room-type" className="h-9" />
          </div>
        )}
        <div className="max-h-72 overflow-auto rounded-md border border-gray-200 text-xs" data-testid="frs-bulk-preview">
          <table className="w-full">
            <thead className="bg-gray-50 sticky top-0">
              <tr className="text-left text-gray-500">
                <th className="px-2 py-1 w-6">
                  <input type="checkbox" data-testid="frs-bulk-check-all"
                         checked={preview.some((p) => p.ok) && preview.filter((p) => p.ok).every((p) => !off.has(p.key))}
                         onChange={(e) => setOff(e.target.checked ? new Set() : new Set(preview.filter((p) => p.ok).map((p) => p.key)))} />
                </th>
                <th className="px-2 py-1">Room</th>
                <th className="px-2 py-1 text-right">Now</th>
                <th className="px-2 py-1 text-right">After</th>
                <th className="px-2 py-1">Note</th>
              </tr>
            </thead>
            <tbody>
              {preview.map((p) => (
                <tr key={p.key} data-testid={`frs-bulk-row-${p.key}`} className={`border-t border-gray-100 ${!p.ok ? "text-gray-400" : ""}`}>
                  <td className="px-2 py-1">
                    <input type="checkbox" disabled={!p.ok} checked={p.ok && !off.has(p.key)} onChange={() => toggle(p.key)}
                           data-testid={`frs-bulk-check-${p.key}`} />
                  </td>
                  <td className="px-2 py-1 text-gray-900">{p.label}</td>
                  <td className="px-2 py-1 text-right tabular-nums">{p.missing ? "—" : p.current}</td>
                  <td className="px-2 py-1 text-right tabular-nums font-semibold">{p.next === null || p.next === "" ? "" : p.next}</td>
                  <td className="px-2 py-1">
                    {mode === "qty" && p.missing && !p.hasScope && `no ${col?.scope || "scope"} in this room — skipped`}
                    {mode === "qty" && p.missing && p.hasScope && (addMissing ? "will be added" : "not in this room")}
                    {mode === "qty" && p.below && <span className="text-red-700">{p.installed} already installed — cannot go lower</span>}
                    {mode === "qty" && !p.missing && !p.below && p.installed > 0 && `${p.installed} installed`}
                    {p.same && p.next !== null && p.next !== "" && "no change"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="flex items-center justify-end gap-2">
          <Button variant="ghost" size="sm" onClick={onClose} data-testid="frs-bulk-cancel">Cancel</Button>
          <Button size="sm" className="bg-amber-600 hover:bg-amber-700" disabled={!staged.length} onClick={stage} data-testid="frs-bulk-stage-btn">
            Stage {staged.length || ""} change{staged.length === 1 ? "" : "s"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
